import { Core } from "@faire/design-tokens";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Storage } from "@faire/web--source/common/Storage";
import { Button } from "@faire/web--source/slate/Button";
import Checkbox from "@faire/web--source/slate/Checkbox";
import { TextArea } from "@faire/web--source/slate/Input/TextArea";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackHelpCenterComprehensiveChatCsatSubmit } from "@faire/web-api--source/events/helpCenter/submit/comprehensiveChatCsat";
import { QueryParameters as chatQueryParams } from "@faire/web-api--source/routes/www/support/self-serve/chat-csat";
import React from "react";
import styled from "styled-components";

import { FAIRE_CHAT_CSAT_FEEDBACK_SUBMITTED_STORAGE_KEY } from "../consts";
import { ChatCsatProvider } from "../hooks/ChatCsatContextStore";
import { initQuestions } from "../questions";
import { QuestionIds, QuestionsById, SurveyType } from "../types";
import { chosenReasons } from "../utils";

import { ChatCsatCollected } from "./ChatCsatCollected";
import { ChatCsatWrapper } from "./ChatCsatWrapper";
import { CsatTitle } from "./CsatTitle";

const EMPTY_AGENT_NAME = "";

export const ChatCsat: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { ticket_id } = useQueryParams(chatQueryParams.parse);
  const surveyType: SurveyType = ticket_id ? "chat" : "email";
  const [questions, setQuestions] = React.useState<
    QuestionsById<typeof surveyType>
  >(() => initQuestions(strictLocalize, surveyType));
  const [feedback, setFeedback] = React.useState<string>("");
  const [feedbackCollected, setFeedbackCollected] =
    React.useState<boolean>(false);
  const retailerToken = getRetailerToken();
  const brandToken = getBrandToken();

  const submitDisabled = () => {
    const allUnchecked = Object.keys(questions).every(
      (key) => !questions[key as QuestionIds<typeof surveyType>].checked
    );
    return allUnchecked && feedback.length === 0;
  };

  const chatCsatHasBeenSubmitted =
    Storage.local.getItem(FAIRE_CHAT_CSAT_FEEDBACK_SUBMITTED_STORAGE_KEY) ===
      "true" && surveyType === "chat";

  const handleCheckboxChange = (
    key: keyof QuestionsById<typeof surveyType>
  ) => {
    setQuestions({
      ...questions,
      [key]: {
        ...questions[key],
        checked: !questions[key].checked,
      },
    });
  };

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    setFeedback(e.target.value);
  };

  const handleSubmitClick = () => {
    // PETE event: https://www.faire.com/pete/event/help_center-comprehensive_chat_csat-submit
    trackHelpCenterComprehensiveChatCsatSubmit(
      brandToken ?? "",
      retailerToken ?? "",
      feedback,
      chosenReasons(questions),
      ticket_id ?? "",
      EMPTY_AGENT_NAME
    );
    setFeedbackCollected(true);
    if (surveyType === "chat") {
      Storage.local.setItem(
        FAIRE_CHAT_CSAT_FEEDBACK_SUBMITTED_STORAGE_KEY,
        "true"
      );
    }
  };

  if (feedbackCollected || chatCsatHasBeenSubmitted) {
    return (
      <ChatCsatProvider value={{ surveyType }}>
        <ChatCsatWrapper>
          <ChatCsatCollected />
        </ChatCsatWrapper>
      </ChatCsatProvider>
    );
  }

  return (
    <ChatCsatProvider value={{ surveyType }}>
      <ChatCsatWrapper>
        <CsatTitle />
        <Spacer height="2x" />
        {Object.keys(questions).map((key) => {
          const question = questions[key as QuestionIds<typeof surveyType>];
          return (
            <React.Fragment key={key}>
              <Checkbox
                {...question}
                onChange={() =>
                  handleCheckboxChange(key as QuestionIds<typeof surveyType>)
                }
                data-test-id={`checkbox-${key}`}
              />
              <Spacer height="2x" />
            </React.Fragment>
          );
        })}
        <Typography>
          <StrictLocalMsg
            defaultMessage="Please provide any other feedback (optional)"
            description={{
              img: "https://cdn.faire.com/fastly/c322d4cd11585c492833dd61b13d958814da01f11b79b5038c89b1a928e1b2a4.png",
            }}
          />
        </Typography>
        <FeedbackTextArea value={feedback} onChange={handleFeedbackChange} />
        <Spacer height="1x" />
        <Typography variant="labelSansRegular" color={Core.text.subdued}>
          <StrictLocalMsg
            defaultMessage="Note: We’re unable to reply directly to any comments provided here. Please contact us through the Help Center for further assistance."
            description={{
              img: "https://cdn.faire.com/fastly/b0e720ebc13dfcb2c3ca8155707dd742e6dbdbf45557de5b3f23885b0800673c.png",
            }}
          />
        </Typography>
        <Spacer height="2x" />
        <SubmitButton onClick={handleSubmitClick} disabled={submitDisabled()}>
          <StrictLocalMsg
            defaultMessage="Submit"
            description={{
              text: "Submit button of the customer satisfaction collection page",

              img: "https://cdn.faire.com/fastly/7c15fe7ae1b4dafba8eda204859c6f3993bc8168f43f6dc2368fad594aa7940b.png",
            }}
          />
        </SubmitButton>
      </ChatCsatWrapper>
    </ChatCsatProvider>
  );
};

const SubmitButton = styled(Button)`
  width: 300px;
  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

const FeedbackTextArea = styled(TextArea)`
  height: 100px;
`;
